/**
 * 상품 관련 API 함수
 */

import type {
  ProductItemDetailDto,
  CourseSummaryDto,
  UpdateProductDto,
  EnrollmentByProductDto,
  PaginatedResponseDto,
  GetEnrollmentsByProductParams,
} from "@repo/types";
import type {
  ProductItemKind,
  ProductKind,
  ProductPackageKind,
  ProductState,
} from "@theailab-dev/parchment";
import apiClient from "../lib/apiClient";

/**
 * 상품 목록 조회 파라미터
 */
export interface GetProductsParams {
  page?: number;
  limit?: number;
  sortBy?: "createdAt" | "updatedAt" | "title";
  sortOrder?: "ASC" | "DESC";
  filterKey?: "search" | "kind" | "state";
  filterValue?: string;
  kind?: ProductKind;
  state?: ProductState;
}

/**
 * 상품 응답 DTO
 */
export interface ProductResponseDto {
  id: number;
  title: string;
  subtitle: string | null;
  slug: string;
  description: string | null;
  kind: ProductKind;
  state: ProductState;
  openAt: Date | null;
  closeAt: Date | null;
  displayEndAt: Date | null;
  totalDuration: string | null;
  thumbnailUrl: string | null;
  imageUrl: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * 페이지네이션 메타데이터
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  itemCount: number;
}

/**
 * 상품 목록 조회 응답
 */
export interface GetProductsResponse {
  data: ProductResponseDto[];
  meta: PaginationMeta;
}

/**
 * 카테고리 간단 정보
 */
export interface CategorySimpleDto {
  id: number;
  title: string;
  name?: string;
}

/**
 * 상품 아이템 DTO
 */
export interface ProductItemDto {
  id: number;
  kind: ProductItemKind;
  state: string;
  courseId: number | null;
  categoryId: number | null;
  category: CategorySimpleDto | null;
  originalPrice: number;
  seq: number;
  discount: number;
  packageKind?: ProductPackageKind; // product_package.kind (상품 패키지 유형)
  createdAt: Date;
  updatedAt: Date;
  course?: CourseSummaryDto | null;
}

/**
 * 상품 상세 DTO
 */
export interface ProductDetailDto {
  id: number;
  title: string;
  subtitle: string | null;
  slug: string;
  description: string | null;
  kind: ProductKind;
  state: ProductState;
  openAt: Date | null;
  closeAt: Date | null;
  displayEndAt: Date | null;
  totalDuration: string | null;
  thumbnailUrl: string | null;
  imageUrl: string | null;
  createdAt: Date;
  updatedAt: Date;
  items: ProductItemDto[];
}

/**
 * 상품 목록 조회
 *
 * 엔드포인트: GET /api/product
 * - 페이지네이션, 정렬, 필터링 지원
 * - 검색(제목/설명), 유형 필터링 가능
 *
 * @param params - 조회 파라미터 (페이지, 정렬, 필터 등)
 * @returns 상품 목록 및 페이지네이션 메타데이터
 * @throws API 요청 실패 시 에러
 */
export const getProducts = async (
  params: GetProductsParams
): Promise<GetProductsResponse> => {
  try {
    console.log("📤 상품 목록 API 요청:", {
      url: "/product",
      params,
    });

    const response = await apiClient.get<GetProductsResponse>("/product", {
      params,
    });

    console.log("📦 상품 목록 조회 성공:", {
      총개수: response.data.meta.total,
      현재페이지: response.data.meta.page,
      데이터개수: response.data.data.length,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 상품 목록 조회 실패:", error);
    throw error;
  }
};

/**
 * 상품 상세 조회
 *
 * 엔드포인트: GET /api/product/:id
 * - 상품 기본 정보와 ProductItem 목록 포함
 *
 * @param productId - 상품 ID
 * @returns 상품 상세 정보
 * @throws API 요청 실패 시 에러
 */
export const getProductDetail = async (
  productId: number
): Promise<ProductDetailDto> => {
  try {
    console.log("📤 상품 상세 조회 요청:", { productId });

    const response = await apiClient.get<ProductDetailDto>(
      `/product/${productId}`
    );

    console.log("📦 상품 상세 조회 성공:", {
      id: response.data.id,
      title: response.data.title,
      itemsCount: response.data.items.length,
      items: response.data.items.map((item) => ({
        id: item.id,
        courseId: item.courseId,
        originalPrice: item.originalPrice,
        discount: item.discount,
      })),
    });
    return response.data;
  } catch (error) {
    console.error("❌ 상품 상세 조회 실패:", error);
    throw error;
  }
};

/**
 * 상품 아이템 상세 조회
 *
 * 엔드포인트: GET /api/product-item/:id
 * - 상품 아이템 기본 정보와 강의 간략 정보 포함 (courseId가 있는 경우)
 *
 * @param productItemId - 상품 아이템 ID
 * @returns 상품 아이템 상세 정보
 * @throws API 요청 실패 시 에러
 */
export const getProductItemDetail = async (
  productItemId: number
): Promise<ProductItemDetailDto> => {
  try {
    console.log("📤 상품 아이템 상세 조회 요청:", { productItemId });

    const response = await apiClient.get<ProductItemDetailDto>(
      `/product/item/${productItemId}`
    );

    console.log("📦 상품 아이템 상세 조회 성공:", {
      id: response.data.id,
      kind: response.data.kind,
      hasCourse: !!response.data.course,
      discount: response.data.discount,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 상품 아이템 상세 조회 실패:", error);
    throw error;
  }
};

/**
 * 상품 수정
 *
 * 엔드포인트: PATCH /api/product/:id
 *
 * @param productId - 상품 ID
 * @param data - 수정할 데이터 (UpdateProductDto)
 * @param thumbnailFile - 썸네일 파일 (선택)
 * @param imageFile - 이미지 파일 (선택)
 * @returns 수정된 상품 정보
 * @throws API 요청 실패 시 에러
 */
export const updateProduct = async (
  productId: number,
  data: UpdateProductDto,
  thumbnailFile?: File,
  imageFile?: File
): Promise<ProductDetailDto> => {
  try {
    console.log("📤 상품 수정 요청:", {
      productId,
      data,
      hasThumbnailFile: !!thumbnailFile,
      hasImageFile: !!imageFile,
    });

    let response;

    // 파일이 있으면 FormData로 전송
    if (thumbnailFile || imageFile) {
      const formData = new FormData();

      // 텍스트 필드 추가
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof Date) {
            formData.append(key, value.toISOString());
          } else {
            formData.append(key, String(value));
          }
        }
      });

      // 파일 추가
      if (thumbnailFile) {
        formData.append("thumbnail", thumbnailFile);
      }
      if (imageFile) {
        formData.append("image", imageFile);
      }

      response = await apiClient.patch<ProductDetailDto>(
        `/product/${productId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      // 파일이 없으면 JSON으로 전송
      response = await apiClient.patch<ProductDetailDto>(
        `/product/${productId}`,
        data
      );
    }

    console.log("📦 상품 수정 성공:", {
      id: response.data.id,
      title: response.data.title,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 상품 수정 실패:", error);
    throw error;
  }
};

/**
 * 상품 생성
 *
 * 엔드포인트: POST /api/product
 *
 * @param data - 상품 생성 데이터 (CreateProductDto)
 * @param thumbnailFile - 썸네일 파일 (선택)
 * @param imageFile - 이미지 파일 (선택)
 * @returns 생성된 상품 정보
 * @throws API 요청 실패 시 에러
 */
export interface CreateProductDto {
  title: string;
  slug: string;
  description?: string | null;
  kind: ProductKind;
  state?: ProductState;
  openAt?: Date | null;
  closeAt?: Date | null;
  displayEndAt?: Date | null;
  productItemId?: number | null; // 단일 상품(SINGLE) 생성 시 필수
}

export const createProduct = async (
  data: CreateProductDto,
  thumbnailFile?: File,
  imageFile?: File
): Promise<ProductDetailDto> => {
  try {
    console.log("📤 상품 생성 요청:", {
      data,
      hasThumbnailFile: !!thumbnailFile,
      hasImageFile: !!imageFile,
    });

    let response;

    // 파일이 있으면 FormData로 전송
    if (thumbnailFile || imageFile) {
      const formData = new FormData();

      // 텍스트 필드 추가 (productItemId는 별도 처리)
      Object.entries(data).forEach(([key, value]) => {
        // productItemId는 별도로 처리하므로 여기서 제외
        if (key === "productItemId") {
          return;
        }
        if (value !== undefined && value !== null) {
          if (value instanceof Date) {
            formData.append(key, value.toISOString());
          } else {
            formData.append(key, String(value));
          }
        }
      });

      // productItemId가 있으면 명시적으로 추가 (숫자로 변환하여 검증)
      if (data.productItemId !== undefined && data.productItemId !== null) {
        const productItemId = Number(data.productItemId);
        if (
          !isNaN(productItemId) &&
          productItemId > 0 &&
          Number.isInteger(productItemId)
        ) {
          formData.append("productItemId", String(productItemId));
          console.log("✅ FormData에 productItemId 추가:", productItemId);
        } else {
          console.error("❌ 유효하지 않은 productItemId:", {
            original: data.productItemId,
            converted: productItemId,
            isNaN: isNaN(productItemId),
            isPositive: productItemId > 0,
            isInteger: Number.isInteger(productItemId),
          });
        }
      } else {
        console.log("⚠️ productItemId가 없습니다:", {
          productItemId: data.productItemId,
          kind: data.kind,
        });
      }

      // FormData 내용 확인 (디버깅용)
      console.log("📋 FormData 내용:", {
        hasProductItemId: formData.has("productItemId"),
        productItemIdValue: formData.get("productItemId"),
        allKeys: Array.from(formData.keys()),
      });

      // 파일 추가
      if (thumbnailFile) {
        formData.append("thumbnail", thumbnailFile);
      }
      if (imageFile) {
        formData.append("image", imageFile);
      }

      response = await apiClient.post<ProductDetailDto>("/product", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      // 파일이 없으면 JSON으로 전송
      response = await apiClient.post<ProductDetailDto>("/product", data);
    }

    console.log("✅ 상품 생성 성공:", {
      id: response.data.id,
      title: response.data.title,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 상품 생성 실패:", error);
    throw error;
  }
};

/**
 * 상품 아이템 생성
 *
 * 엔드포인트: POST /api/product/:productId/product-item
 *
 * @param productId - 상품 ID
 * @param data - 상품 아이템 생성 데이터
 * @returns 생성된 상품 아이템 정보
 * @throws API 요청 실패 시 에러
 *
 * 참고:
 * - originalPrice: product_item 테이블의 original_price 컬럼에 저장
 * - discount: product_package 테이블의 discount 컬럼에 저장 (상품과 상품 아이템을 연결하는 중간 테이블)
 */
export interface CreateProductItemDto {
  courseId: number | null;
  kind: ProductItemKind;
  originalPrice: number; // product_item.original_price
  discount?: number; // product_package.discount (상품 생성 시 product_package에 저장됨)
  packageKind?: ProductPackageKind; // product_package.kind (상품 패키지 유형)
}

/**
 * 슬러그 중복 확인
 *
 * 엔드포인트: GET /api/product/slug/:slug/check
 *
 * @param slug - 확인할 슬러그
 * @returns 중복 여부 (true: 사용 가능, false: 중복)
 * @throws API 요청 실패 시 에러
 */
export const checkSlugAvailability = async (
  slug: string
): Promise<{ available: boolean }> => {
  try {
    const response = await apiClient.get<{ available: boolean }>(
      `/product/slug/${slug}/check`
    );
    return response.data;
  } catch (error) {
    console.error("❌ 슬러그 중복 확인 실패:", error);
    throw error;
  }
};

export const createProductItem = async (
  productId: number,
  data: CreateProductItemDto
): Promise<ProductItemDto> => {
  try {
    console.log("📤 상품 아이템 생성 요청:", { productId, data });

    const response = await apiClient.post<ProductItemDto>(
      `/product/${productId}/product-item`,
      data
    );

    console.log("✅ 상품 아이템 생성 성공:", {
      id: response.data.id,
      courseId: response.data.courseId,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 상품 아이템 생성 실패:", error);
    throw error;
  }
};

/**
 * 상품 아이템 수정 DTO
 *
 * 참고:
 * - originalPrice: product_item 테이블의 original_price 컬럼 수정
 * - discount: product_package 테이블의 discount 컬럼 수정 (상품과 상품 아이템을 연결하는 중간 테이블)
 */
export interface UpdateProductItemDto {
  productItemId?: number; // 사용자가 선택한 새로운 ProductItem ID (product_package.product_item_id 변경용)
  originalPrice?: number; // product_item.original_price
  discount?: number; // product_package.discount (상품 패키지의 할인가)
  packageKind?: ProductPackageKind; // product_package.kind (상품 패키지 유형)
}

/**
 * 상품 아이템 수정
 *
 * 엔드포인트: PATCH /api/product/:productId/product-item/:itemId
 *
 * @param productId - 상품 ID
 * @param productItemId - 상품 아이템 ID
 * @param data - 수정할 데이터 (courseId, originalPrice, discount)
 * @returns 수정된 상품 아이템 정보
 * @throws API 요청 실패 시 에러
 */
export const updateProductItem = async (
  productId: number,
  productItemId: number,
  data: UpdateProductItemDto
): Promise<ProductItemDetailDto> => {
  try {
    console.log("📤 상품 아이템 수정 요청:", {
      productId,
      currentProductItemId: productItemId,
      data,
      discount: data.discount,
      packageKind: data.packageKind,
      newProductItemId: data.productItemId,
    });

    const response = await apiClient.patch<ProductItemDetailDto>(
      `/product/${productId}/product-item/${productItemId}`,
      data
    );

    console.log("📦 상품 아이템 수정 응답 전체:", {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      dataType: typeof response.data,
      dataKeys: response.data ? Object.keys(response.data) : [],
    });
    console.log("📦 응답 데이터 상세:", JSON.stringify(response.data, null, 2));

    // 응답 데이터 검증
    if (!response.data) {
      console.error("❌ 응답 데이터가 없습니다:", response);
      throw new Error("상품 아이템 수정 응답 데이터가 없습니다.");
    }

    // 응답 데이터 구조 확인
    const responseData = response.data as any;

    // 응답이 {success: true} 형태인 경우, 실제 데이터는 없을 수 있음
    // 이 경우 요청한 데이터를 그대로 반환하거나, 상품 상세를 다시 조회해야 함
    if (responseData.success === true && !responseData.data) {
      console.log("✅ 상품 아이템 수정 성공 (success 응답)");
      // 성공 응답만 오는 경우, 요청한 데이터를 포함한 객체 반환
      return {
        id: productItemId,
        discount: data.discount ?? 0,
        packageKind: data.packageKind,
        ...responseData,
      } as ProductItemDetailDto;
    }

    // 일반적인 응답 구조 처리
    const discount =
      responseData.discount ??
      responseData.productPackage?.discount ??
      responseData.package?.discount ??
      data.discount;
    const packageKind =
      responseData.packageKind ??
      responseData.productPackage?.kind ??
      responseData.package?.kind ??
      data.packageKind;
    const id =
      responseData.id ??
      responseData.productItemId ??
      responseData.itemId ??
      productItemId;

    console.log("✅ 상품 아이템 수정 성공:", {
      id,
      discount,
      packageKind,
      fullData: responseData,
    });

    // 응답 데이터에 discount와 packageKind가 없으면 요청 데이터로 채움
    const result = {
      ...responseData,
      id: id,
      discount: discount ?? data.discount ?? 0,
      packageKind: packageKind ?? data.packageKind,
    };

    return result as ProductItemDetailDto;
  } catch (error) {
    console.error("❌ 상품 아이템 수정 실패:", error);
    throw error;
  }
};

/**
 * 상품 아이템 삭제
 *
 * 엔드포인트: DELETE /api/product/:productId/product-item/:itemId
 *
 * @param productId - 상품 ID
 * @param productItemId - 상품 아이템 ID
 * @returns 없음
 * @throws API 요청 실패 시 에러
 */
export const deleteProductItem = async (
  productId: number,
  productItemId: number
): Promise<void> => {
  try {
    console.log("📤 상품 아이템 삭제 요청:", { productId, productItemId });

    await apiClient.delete(
      `/product/${productId}/product-item/${productItemId}`
    );

    console.log("✅ 상품 아이템 삭제 성공");
  } catch (error) {
    console.error("❌ 상품 아이템 삭제 실패:", error);
    throw error;
  }
};

/**
 * 상품 삭제
 *
 * 엔드포인트: DELETE /api/product/:id
 *
 * @param productId - 상품 ID
 * @throws API 요청 실패 시 에러
 */
export const deleteProduct = async (productId: number): Promise<void> => {
  try {
    console.log("📤 상품 삭제 요청:", { productId });

    await apiClient.delete(`/product/${productId}`);

    console.log("✅ 상품 삭제 성공:", {
      productId,
    });
  } catch (error) {
    console.error("❌ 상품 삭제 실패:", error);
    throw error;
  }
};

/**
 * 상품 아이템 순서 변경
 *
 * 엔드포인트: PATCH /api/product/:productId/product-item/reorder
 */
export const reorderProductItems = async (
  productId: number,
  itemIds: number[]
): Promise<void> => {
  try {
    console.log("📤 상품 아이템 순서 변경 요청:", { productId, itemIds });

    await apiClient.patch(`/product/${productId}/product-item/reorder`, {
      itemIds,
    });

    console.log("✅ 상품 아이템 순서 변경 성공");
  } catch (error) {
    console.error("❌ 상품 아이템 순서 변경 실패:", error);
    throw error;
  }
};

/**
 * 상품별 수강 목록 조회
 *
 * @param productId - 상품 ID
 * @param params - 페이지네이션 파라미터 (page, limit)
 * @returns 페이지네이션된 수강 목록
 *
 * 엔드포인트: GET /api/product/:productId/enrollments
 */
export const getProductEnrollments = async (
  productId: number,
  params: GetEnrollmentsByProductParams
): Promise<PaginatedResponseDto<EnrollmentByProductDto>> => {
  try {
    console.log("📤 상품 수강 목록 조회 요청:", { productId, params });

    const response = await apiClient.get<PaginatedResponseDto<EnrollmentByProductDto>>(
      `/product/${productId}/enrollments`,
      {
        params,
      }
    );

    console.log("✅ 상품 수강 목록 조회 성공:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ 상품 수강 목록 조회 실패:", error);
    throw error;
  }
};
